import { useEffect, useState } from "react";
import { Loader2, Check, UserCog } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useProfil, useUpdateProfil } from "@/lib/backoffice-hooks";

export interface ProfilDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProfilDialog({ open, onOpenChange }: ProfilDialogProps) {
  const profil = useProfil({ enabled: open });
  const update = useUpdateProfil();

  const [form, setForm] = useState({
    telephone: "",
    email: "",
    profession: "",
    adresse: "",
  });

  // Préremplit depuis le profil chargé (à l'ouverture / quand les données arrivent).
  useEffect(() => {
    if (open && profil.data) {
      setForm({
        telephone: profil.data.telephone ?? "",
        email: profil.data.email ?? "",
        profession: profil.data.profession ?? "",
        adresse: profil.data.adresse ?? "",
      });
    }
  }, [open, profil.data]);

  function submit(ev: React.FormEvent) {
    ev.preventDefault();
    update.mutate(
      {
        telephone: form.telephone.trim(),
        email: form.email.trim(),
        profession: form.profession.trim(),
        adresse: form.adresse.trim(),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0">
        <div className="shrink-0 bg-navy px-6 py-5 text-white">
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold text-white">
            <UserCog size={22} /> Mon profil
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm text-white/70">
            Modifiez vos coordonnées de contact.
          </DialogDescription>
        </div>

        <div className="overflow-y-auto px-6 pb-6 pt-5">
          {profil.isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-navy/60">
              <Loader2 size={18} className="animate-spin" /> Chargement du profil...
            </div>
          ) : profil.error ? (
            <p className="py-8 text-sm text-red-600">
              Impossible de charger votre profil. Veuillez réessayer.
            </p>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="profil-telephone">Téléphone</Label>
                  <Input
                    id="profil-telephone"
                    value={form.telephone}
                    onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="profil-email">Email</Label>
                  <Input
                    id="profil-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="profil-profession">Profession</Label>
                <Input
                  id="profil-profession"
                  value={form.profession}
                  onChange={(e) => setForm((f) => ({ ...f, profession: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="profil-adresse">Adresse</Label>
                <Input
                  id="profil-adresse"
                  value={form.adresse}
                  onChange={(e) => setForm((f) => ({ ...f, adresse: e.target.value }))}
                />
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-navy text-white hover:bg-navy/90"
                  disabled={update.isPending}
                >
                  {update.isPending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Enregistrement...
                    </>
                  ) : (
                    <>
                      <Check size={16} /> Enregistrer
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
